<template>
  <div class="gallery">
    <div class="gallery__header gallery__columns">
      <div class="gallery__column">
        <p class="gallery__heading" :style="themeStyles">Gallery</p>
        <p
          class="gallery__episode-count"
          :class="`gallery__episode-count--${this.theme.type}`"
          :style="episodeCountStyles"
        >
          {{ episodeCountText }}
        </p>
      </div>
      <div class="gallery__column flex-end">
        <button
          class="gallery__button"
          :class="`gallery__button--${theme.type}`"
          @click="handleSearchClick"
        >
          <span class="is-sr-only">Search</span>
          <span
            v-html="searchIconSvg"
            class="gallery__svg gallery__svg--search"
          ></span>
        </button>

        <button
          class="gallery__button"
          :class="`gallery__button--${theme.type}`"
          @click="handleSortClick"
        >
          <span class="is-sr-only">Sort {{ nextSortAction }}</span>
          <span v-html="sortSvg" class="gallery__svg gallery__svg--sort"></span>
        </button>
      </div>
    </div>
    <search-actions-bar
      :is-visible="showingFilteredPodcasts"
      :search-term="searchTerm"
      :handle-clear-search="handleClearSearch"
      :theme="theme"
    ></search-actions-bar>
    <div class="gallery__body">
      <div class="gallery__pages">
        <div
          v-for="(i, page) in pages"
          :key="i"
          :id="`gallery__page--${i}`"
          class="gallery__page"
          :class="{ 'gallery__page--active': i === 0 }"
        >
          <gallery-item
            v-for="item in page"
            :key="item.id"
            :item="item"
            :is-playing="isPlaying && item.id === podcastId"
            :is-loading="isLoading && item.id === podcastId"
            :is-classic="isClassic"
            :theme="theme"
            :show-title="showTitle"
            @play="handlePlay"
            @pause="$emit('pause')"
          ></gallery-item>
        </div>
      </div>
    </div>
    <div class="gallery__footer">
      <pagination
        :count="pages.length"
        :current-page="currentPage"
        :theme="theme"
        @change-page="changePage"
        v-ref:pagination
      ></pagination>
    </div>
  </div>
</template>

<script>
import Item from './Item';
import SearchActionsBar from './SearchActionsBar';
import Pagination from './Pagination';

export default {
  name: 'gallery',
  components: {
    'gallery-item': Item,
    SearchActionsBar,
    Pagination,
  },
  props: {
    theme: {
      type: Object,
      validator(value) {
        return ['light', 'dark', 'custom'].includes(value.type);
      },
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    isClassic: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isPlaying: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isLoading: {
      type: Boolean,
      default() {
        return false;
      },
    },
    podcastId: Number,
    handleSearchClick: {
      type: Function,
      default: () => {},
    },
    showingFilteredPodcasts: {
      type: Boolean,
      default: false,
    },
    handleClearSearch: {
      type: Function,
      default: () => {},
    },
    searchTerm: {
      type: String,
      default: undefined,
    },
    showTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      sortMode: 'desc', // 'desc' || 'asc'
      currentPage: 0,
      galleryItems: this.items,
    };
  },
  computed: {
    nextSortAction() {
      return this.sortMode === 'desc' ? 'Ascending' : 'Descending';
    },

    searchIconSvg() {
      const fill = this.getFillColor();
      return `
        <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12.5 11.9937H11.71L11.43 11.7237C12.41 10.5837 13 9.10365 13 7.49365C13 3.90365 10.09 0.993652 6.5
            0.993652C2.91 0.993652 0 3.90365 0 7.49365C0 11.0837 2.91 13.9937 6.5 13.9937C8.11 13.9937 9.59 13.4037
            10.73 12.4237L11 12.7037V13.4937L16 18.4837L17.49 16.9937L12.5 11.9937ZM6.5 11.9937C4.01 11.9937 2 9.98365 2
            7.49365C2 5.00365 4.01 2.99365 6.5 2.99365C8.99 2.99365 11 5.00365 11 7.49365C11 9.98365 8.99 11.9937 6.5
            11.9937Z"
            fill="${fill}"
          />
        </svg>`;
    },

    sortSvg() {
      const fill = this.getFillColor();
      return `
        <svg width="15" height="19" viewBox="0 0 15 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M7.00012 0.993652C6.72824 0.993652 6.45567 1.09625 6.24817 1.30225L0.312618 7.19287C-0.356382 7.85687
            0.117571 8.99365 1.06457 8.99365H12.9357C13.8827 8.99365 14.3576 7.85687 13.6876 7.19287L7.75207
            1.30225C7.54407 1.09625 7.27199 0.993652 7.00012 0.993652ZM1.06457 10.9937C0.117571 10.9937 -0.355429
            12.1304 0.314571 12.7944L6.24817 18.6851C6.66317 19.0971 7.33707 19.0971 7.75207 18.6851L13.6857
            12.7944C14.3547 12.1304 13.8817 10.9937 12.9357 10.9937H1.06457Z"
            fill="${fill}"
          />
        </svg>`;
    },

    episodeCountText() {
      const length = this.items.length || 0;
      const episodes = length === 1 ? 'Episode' : 'Episodes';
      return `${length} ${episodes}`;
    },

    themeStyles() {
      return {
        color: this.theme.text,
      };
    },

    episodeCountStyles() {
      if (this.theme.type !== 'custom') {
        return null;
      }

      return {
        color: this.theme.text,
      };
    },

    /**
     * Pages containing gallery items
     *
     * @return {Array}
     */
    pages() {
      const items = [...this.items];
      const pages = [];

      while (items.length >= 1) {
        pages.push(items.splice(0, 3));
      }
      return pages;
    },
  },
  watch: {
    /**
     * listen to the current playing podcast
     * in order to trigger a page change if that
     * pod exists on a different page
     */
    podcastId() {
      const pageContainingCurrentPodcast = this.pages.findIndex((page) => {
        return page.find((pod) => pod.id === this.podcastId);
      });
      this.changePage(pageContainingCurrentPodcast);
    },
  },
  methods: {
    handleSortClick() {
      this.sortMode = this.sortMode === 'desc' ? 'asc' : 'desc';
      this.galleryItems.reverse();
      this.currentPage = 0; // force user back to page 1
    },

    getFillColor() {
      let fill;
      switch (this.theme.type) {
        case 'dark':
          fill = '#ffffff';
          break;

        case 'custom':
          fill = this.theme.text;
          break;

        default:
          fill = '#222046'; // default is light mode
      }
      return fill;
    },

    handlePlay(event) {
      this.$emit('load-podcast', event);
    },

    /**
     * Changes the page
     *
     * @param {Number} page the page to change to -- zero based index
     * @return {void}
     */
    changePage(page) {
      this.$el
        .querySelector(`#gallery__page--${this.currentPage}`)
        .classList.remove('gallery__page--active');
      this.currentPage = page;
      this.$el
        .querySelector(`#gallery__page--${this.currentPage}`)
        .classList.add('gallery__page--active');
      this.$refs.pagination.showMorePages = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/_variables-v2';
@import '../../assets/_helpers';

.gallery {
  font-family: $font;
  margin-bottom: 1rem;
  &__columns {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 -0.75rem;
  }

  &__column {
    flex: 1 0 auto;
    padding: 0 0.75rem;
  }

  &__header {
    margin-bottom: 1rem;
  }

  &__heading {
    display: inline-block;
    color: #222046;
    font-weight: 500;
    font-size: 1.25rem;
  }

  &__episode-count {
    display: inline-block;
    padding-left: 2px;
    &--light {
      color: #6e6e7a;
    }
    &--dark {
      color: #f5f5f5;
    }
  }

  &__button {
    background: transparent;
    border: 0;
    padding: 5px;
    margin-left: 0.75rem;
    border-radius: 50%;
    width: 35px;
    height: 35px;
    cursor: pointer;
    &--light {
      &:hover,
      &:focus:not(:active) {
        opacity: 0.8;
        background: #e3e3e3;
      }
    }
    &--dark {
      &:hover,
      &:focus:not(:active) {
        opacity: 0.8;
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__svg {
    display: block;
    &--search {
      transform: translateY(2px); // added for optical balance
    }
    &--sort {
      transform: translate(1px, 2px); // added to center
    }
  }

  &__page {
    display: none;
    visibility: hidden;
    &--active {
      display: block;
      visibility: visible;
    }
  }

  &__body {
    margin-bottom: 1.5rem;
    min-height: 180px;
  }
}
</style>



// WEBPACK FOOTER //
// Gallery.vue?625c85dc